import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { CalendarCheck, ChevronLeft, ChevronRight, Plus, Settings } from "lucide-react";

import { orpc } from "@/client";
import type { AgendaEvent } from "@/api/db/agenda";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton, SkeletonRows } from "@/components/ui/skeleton";
import { AgendaLabels } from "@/lib/labels/agenda";
import { fallback } from "@/lib/search-params";
import { djs } from "@/lib/djs";
import { format } from "@/lib/format";

import { AgendaEventModal } from "./-components/agenda-event-modal";
import { AgendaTodayStrip } from "./-components/agenda-today-strip";
import { AgendaView } from "./-components/agenda-view";
import { AgendaWeekGrid } from "./-components/agenda-week-grid";
import { AgendaCreateModal } from "./-components/agenda-create-modal";
import { AgendaAttendanceModal } from "./-components/agenda-attendance-modal";
import { AgendaSettingsModal } from "./-components/agenda-settings-modal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const agendaSearch = type({
	semana: fallback(type("number.integer"), 0),
});

export const Route = createFileRoute("/_app/agenda")({
	validateSearch: agendaSearch,
	component: AgendaPage,
});

const NAV_BUTTON = "control-button button-outline size-11 md:size-10";

function GridSkeleton() {
	return (
		<div className="p-4">
			<div className="mb-3 grid grid-cols-7 gap-2">
				{Array.from({ length: 7 }, (_, index) => (
					<Skeleton key={index} className="h-5 w-full" />
				))}
			</div>
			<Skeleton className="h-72 w-full" />
		</div>
	);
}

function AgendaPage() {
	const navigate = Route.useNavigate();
	const { semana } = Route.useSearch();
	const [selected, setSelected] = useState<AgendaEvent | null>(null);
	const [creating, setCreating] = useState(false);
	const [registeringAttendance, setRegisteringAttendance] = useState(false);
	const [configuring, setConfiguring] = useState(false);
	const [mobileView, setMobileView] = useState<"day" | "week">("day");

	const today = format.today();
	const tomorrow = djs(today, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
	const week = AgendaView.week(today, semana);

	const strip = useQuery(
		orpc.agenda.list.queryOptions({
			input: { de: today, ate: tomorrow, force: false },
			placeholderData: keepPreviousData,
		}),
	);

	const grid = useQuery(
		orpc.agenda.list.queryOptions({
			input: { de: week.de, ate: week.ate, force: false },
			placeholderData: keepPreviousData,
		}),
	);

	const eventsByDate = new Map((grid.data?.days ?? []).map((day) => [day.date, day.events]));
	const weekTotal = (grid.data?.days ?? []).reduce((total, day) => total + day.events.length, 0);

	return (
		<PageContainer>
			<PageHeader
				title="Agenda"
				actions={
					<>
						<Button variant="outline" onClick={() => setRegisteringAttendance(true)}>
							<CalendarCheck />
							Atendimento
						</Button>
						<Button variant="outline" onClick={() => setConfiguring(true)}>
							<Settings />
							Configurar
						</Button>
						<Button onClick={() => setCreating(true)}>
							<Plus />
							Novo horário
						</Button>
					</>
				}
			/>

			{!strip.data && strip.isError && (
				<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-12 text-center">
					<p className="text-sm text-destructive">{AgendaLabels.loadError}</p>
				</div>
			)}

			{!strip.data && !strip.isError && <SkeletonRows rows={4} />}

			{!!strip.data && (
				<div className="min-w-0">
					<div className="segmented-control mb-3 grid grid-cols-2 lg:hidden">
						<button
							type="button"
							aria-pressed={mobileView === "day"}
							onClick={() => setMobileView("day")}
							className={cn(
								"segmented-item",
								mobileView === "day"
									? "bg-primary text-primary-foreground"
									: "text-muted-foreground",
							)}
						>
							Dia
						</button>
						<button
							type="button"
							aria-pressed={mobileView === "week"}
							onClick={() => setMobileView("week")}
							className={cn(
								"segmented-item",
								mobileView === "week"
									? "bg-primary text-primary-foreground"
									: "text-muted-foreground",
							)}
						>
							Semana
						</button>
					</div>

					<div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
						<div
							className={cn("order-1 min-w-0", mobileView === "day" ? "hidden lg:block" : "block")}
						>
							<div className="surface-panel overflow-hidden">
								<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3">
									<div className="flex flex-wrap items-center gap-2">
										<button
											type="button"
											aria-label={AgendaLabels.prevWeek}
											onClick={() =>
												navigate({
													search: (prev) => ({ ...prev, semana: prev.semana - 1 }),
												})
											}
											className={NAV_BUTTON}
										>
											<ChevronLeft className="size-4" />
										</button>
										<button
											type="button"
											aria-label={AgendaLabels.nextWeek}
											onClick={() =>
												navigate({
													search: (prev) => ({ ...prev, semana: prev.semana + 1 }),
												})
											}
											className={NAV_BUTTON}
										>
											<ChevronRight className="size-4" />
										</button>
										<p className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
											<span>
												<span className="font-sans">de </span>
												<time dateTime={week.de}>
													{djs(week.de, "YYYY-MM-DD").format("DD/MM/YYYY")}
												</time>
											</span>
											<span>
												<span className="font-sans">até </span>
												<time dateTime={week.ate}>
													{djs(week.ate, "YYYY-MM-DD").format("DD/MM/YYYY")}
												</time>
											</span>
										</p>
										{semana !== 0 && (
											<button
												type="button"
												onClick={() => navigate({ search: (prev) => ({ ...prev, semana: 0 }) })}
												className="control-button button-outline min-h-11 px-3 py-2 text-xs md:min-h-9"
											>
												{AgendaLabels.backToCurrentWeek}
											</button>
										)}
									</div>
								</div>

								{grid.isPending && <GridSkeleton />}

								{!grid.isPending && weekTotal > 0 && (
									<AgendaWeekGrid
										days={week.days}
										eventsByDate={eventsByDate}
										today={today}
										selectedHash={selected?.hash}
										onSelect={setSelected}
									/>
								)}

								{!grid.isPending && weekTotal === 0 && (
									<div className="p-12 text-center text-sm text-muted-foreground">
										{AgendaLabels.emptyWeek}
									</div>
								)}
							</div>
						</div>

						<aside
							className={cn(
								"order-2 min-w-0 lg:max-h-[38rem] lg:overflow-y-auto lg:overscroll-contain",
								mobileView === "week" ? "hidden lg:block" : "block",
							)}
						>
							<AgendaTodayStrip days={strip.data.days} today={today} onSelect={setSelected} />
						</aside>
					</div>
				</div>
			)}

			{selected !== null && <AgendaEventModal event={selected} onClose={() => setSelected(null)} />}
			{creating && <AgendaCreateModal onClose={() => setCreating(false)} />}
			{registeringAttendance && (
				<AgendaAttendanceModal onClose={() => setRegisteringAttendance(false)} />
			)}
			{configuring && <AgendaSettingsModal onClose={() => setConfiguring(false)} />}
		</PageContainer>
	);
}
